// src/hooks/useApprovals.js
// Manages the approval workflow queue — submit, approve, reject, list.

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  orderBy,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { logger } from '../utils/logger';

export function useApprovals() {
  const { firebaseUser, teamOwnerId, currentUserRole } = useAuth();
  const ownerId = teamOwnerId || firebaseUser?.uid;

  const [approvals, setApprovals] = useState([]);
  const [approvalsLoading, setApprovalsLoading] = useState(true);

  // ── Real-time listener for approvals ────────────────────────
  useEffect(() => {
    if (!ownerId) {
      setApprovalsLoading(false);
      return;
    }

    const q = query(
      collection(db, 'approvals'),
      where('ownerId', '==', ownerId),
      orderBy('requestedAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setApprovals(data);
        setApprovalsLoading(false);
      },
      (error) => {
        logger.error('Error loading approvals:', error);
        setApprovalsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [ownerId]);

  // ── Submit a new approval request ───────────────────────────
  const submitApproval = useCallback(
    async ({ type, projectId, projectAddress, data, addNotification }) => {
      if (!ownerId || !firebaseUser) return { success: false, error: 'Not authenticated' };

      const typeLabels = {
        lead_conversion: 'Lead Conversion',
        budget_change: 'Budget Change',
        listing_approval: 'Listing Approval',
        price_change: 'Price Change',
        sale_approval: 'Sale Finalization',
        status_change: 'Status Change',
        completion_gate: 'Completion Gate',
        cfo_review: 'CFO Review',
        ceo_approval: 'CEO Approval',
      };

      try {
        const approvalDoc = {
          type,
          status: 'pending',
          requestedBy: firebaseUser.uid,
          requestedByName: firebaseUser.displayName || firebaseUser.email,
          requestedAt: serverTimestamp(),
          reviewedBy: null,
          reviewedByName: null,
          reviewedAt: null,
          reviewNotes: '',
          projectId: projectId || null,
          projectAddress: projectAddress || '',
          ownerId,
          data: data || {},
        };

        await addDoc(collection(db, 'approvals'), approvalDoc);

        // Send notification to approvers
        if (addNotification) {
          addNotification({
            title: `Approval Required: ${typeLabels[type] || type}`,
            message: `${projectAddress || 'Unknown'} — requested by ${firebaseUser.displayName || firebaseUser.email}`,
            type: 'approval',
            teamBroadcast: true,
          });
        }

        return { success: true };
      } catch (error) {
        logger.error('Error submitting approval:', error);
        return { success: false, error: error.message };
      }
    },
    [ownerId, firebaseUser]
  );

  // ── Approve a request ───────────────────────────────────────
  const approveRequest = useCallback(
    async (approvalId, notes, addNotification) => {
      if (!firebaseUser) return { success: false };

      try {
        await updateDoc(doc(db, 'approvals', approvalId), {
          status: 'approved',
          reviewedBy: firebaseUser.uid,
          reviewedByName: firebaseUser.displayName || firebaseUser.email,
          reviewedAt: serverTimestamp(),
          reviewNotes: notes || '',
        });

        const approval = approvals.find((a) => a.id === approvalId);
        if (addNotification && approval) {
          addNotification({
            title: 'Approval Granted',
            message: `${approval.projectAddress || 'Request'} has been approved by ${firebaseUser.displayName || 'an admin'}`,
            type: 'success',
            teamBroadcast: true,
          });
        }

        return { success: true };
      } catch (error) {
        logger.error('Error approving request:', error);
        return { success: false, error: error.message };
      }
    },
    [firebaseUser, approvals]
  );

  // ── Reject a request ────────────────────────────────────────
  const rejectRequest = useCallback(
    async (approvalId, notes, addNotification) => {
      if (!firebaseUser) return { success: false };

      try {
        await updateDoc(doc(db, 'approvals', approvalId), {
          status: 'rejected',
          reviewedBy: firebaseUser.uid,
          reviewedByName: firebaseUser.displayName || firebaseUser.email,
          reviewedAt: serverTimestamp(),
          reviewNotes: notes || '',
        });

        const approval = approvals.find((a) => a.id === approvalId);
        if (addNotification && approval) {
          addNotification({
            title: 'Approval Rejected',
            message: `${approval.projectAddress || 'Request'} has been rejected by ${firebaseUser.displayName || 'an admin'}`,
            type: 'info',
            teamBroadcast: true,
          });
        }

        return { success: true };
      } catch (error) {
        logger.error('Error rejecting request:', error);
        return { success: false, error: error.message };
      }
    },
    [firebaseUser, approvals]
  );

  // ── Derived data ────────────────────────────────────────────
  const pendingApprovals = useMemo(
    () => approvals.filter((a) => a.status === 'pending'),
    [approvals]
  );

  const pendingCount = pendingApprovals.length;

  // Check if current user can approve (Owner, Admin, or in approvalRoles)
  const canApprove = useMemo(() => {
    return (
      currentUserRole === 'Owner' || currentUserRole === 'Admin' || currentUserRole === 'Finance'
    );
  }, [currentUserRole]);

  // Check if a specific project has a pending approval of a given type
  const hasPendingApproval = useCallback(
    (projectId, type) => {
      return approvals.some(
        (a) => a.status === 'pending' && a.projectId === projectId && (!type || a.type === type)
      );
    },
    [approvals]
  );

  // Check if a specific project has an approved approval of a given type
  const hasApprovedApproval = useCallback(
    (projectId, type) => {
      return approvals.some(
        (a) => a.status === 'approved' && a.projectId === projectId && (!type || a.type === type)
      );
    },
    [approvals]
  );

  // Get the most recent approval for a project + type (any status)
  const getLatestApproval = useCallback(
    (projectId, type) => {
      return approvals.find((a) => a.projectId === projectId && (!type || a.type === type)) || null; // approvals are ordered by requestedAt desc, so first match is latest
    },
    [approvals]
  );

  return {
    approvals,
    pendingApprovals,
    pendingCount,
    approvalsLoading,
    canApprove,
    submitApproval,
    approveRequest,
    rejectRequest,
    hasPendingApproval,
    hasApprovedApproval,
    getLatestApproval,
  };
}
